// src/pages/Competition/components/CreateCompetitionDrawer.jsx
import React, { useEffect } from "react";
import Button from "../../../components/Button/Button";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const SlideOver = ({ open, onClose, children, title }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative ml-auto h-full w-full max-w-xl bg-white dark:bg-slate-900 shadow-xl
                   border-l border-gray-200 dark:border-slate-700 p-5 sm:p-6
                   transform transition translate-x-0 animate-[mmSlideIn_220ms_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
          <button
            onClick={onClose}
            aria-label="Fermer"
            className="h-8 w-8 rounded-full bg-black/10 dark:bg-white/10 hover:opacity-90 flex items-center justify-center text-gray-700 dark:text-gray-200"
          >
            ×
          </button>
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
};

const quillModules = {
  toolbar: [
    [{ header: [false, 3, 4] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "align",
  "link",
];

const CreateCompetitionDrawer = ({
  open,
  onClose,
  newCompetition,
  setNewCompetition,
  onSubmit,
  isDemoUser,
  isClientUser,
}) => {
  return (
    <SlideOver open={open} onClose={onClose} title="Créer une compétition">
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm mb-1">Nom de la compétition</label>
            <input
              type="text"
              value={newCompetition.nom_competition}
              onChange={(e) =>
                setNewCompetition((s) => ({
                  ...s,
                  nom_competition: e.target.value,
                }))
              }
              required
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Niveau</label>
            <input
              type="text"
              value={newCompetition.level_competition}
              onChange={(e) =>
                setNewCompetition((s) => ({
                  ...s,
                  level_competition: e.target.value,
                }))
              }
              required
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Lieu</label>
            <input
              type="text"
              value={newCompetition.lieu_competition}
              onChange={(e) =>
                setNewCompetition((s) => ({
                  ...s,
                  lieu_competition: e.target.value,
                }))
              }
              required
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Date</label>
            <input
              type="date"
              value={newCompetition.date_competition}
              onChange={(e) =>
                setNewCompetition((s) => ({
                  ...s,
                  date_competition: e.target.value,
                }))
              }
              required
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
            />
          </div>
        </div>

        {/* React Quill pour les horaires de pesées */}
        <div>
          <label className="block text-sm mb-1">
            Horaires de pesées (texte enrichi)
          </label>
          <div className="rounded-md border border-gray-300 dark:border-slate-600 overflow-hidden">
            <ReactQuill
              theme="snow"
              value={newCompetition.horaires_pesees}
              onChange={(html) =>
                setNewCompetition((s) => ({ ...s, horaires_pesees: html }))
              }
              modules={quillModules}
              formats={quillFormats}
              className="min-h-[160px] bg-white dark:bg-slate-800 dark:text-white"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm mb-1">Photo (URL)</label>
          <input
            type="url"
            value={newCompetition.photo_URL}
            onChange={(e) =>
              setNewCompetition((s) => ({ ...s, photo_URL: e.target.value }))
            }
            placeholder="https://…"
            className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2">
            Disciplines autorisées
          </label>
          <div className="flex flex-wrap gap-3">
            {[
              "Judo",
              "Ne-Waza",
              "JJB",
              "Jujitsu Combat",
              "Jujitsu Duo",
              "Arbitre",
              "Commissaire Sportif",
              "Judo Adapté"
            ].map((d) => (
              <label key={d} className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={
                    !!newCompetition.disciplines_competition?.includes(d)
                  }
                  onChange={(e) => {
                    const cur = Array.isArray(
                      newCompetition.disciplines_competition,
                    )
                      ? newCompetition.disciplines_competition
                      : [];
                    const next = e.target.checked
                      ? [...cur, d]
                      : cur.filter((x) => x !== d);
                    setNewCompetition((s) => ({
                      ...s,
                      disciplines_competition: next,
                    }));
                  }}
                />
                {d}
              </label>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <label
            className={`inline-flex items-center gap-2 text-sm ${isDemoUser || isClientUser ? "opacity-60" : ""}`}
          >
            <input
              type="checkbox"
              checked={!!newCompetition.is_active}
              onChange={(e) =>
                setNewCompetition((s) => ({
                  ...s,
                  is_active: e.target.checked,
                }))
              }
              disabled={isDemoUser || isClientUser}
            />
            Activer la compétition
          </label>
          <label
            className={`inline-flex items-center gap-2 text-sm ${isDemoUser || isClientUser ? "opacity-60" : ""}`}
          >
            <input
              type="checkbox"
              checked={!!newCompetition.is_public}
              onChange={(e) =>
                setNewCompetition((s) => ({
                  ...s,
                  is_public: e.target.checked,
                }))
              }
              disabled={isDemoUser || isClientUser}
            />
            Rendre la compétition publique
          </label>
        </div>

        <div className="pt-2 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-lg">
            Annuler
          </Button>
          <Button type="submit" className="rounded-lg">
            Créer la compétition
          </Button>
        </div>
      </form>
    </SlideOver>
  );
};

export default CreateCompetitionDrawer;
